import { useState, useEffect } from "react";
import { User, Target, UserPlus, AlertTriangle, Shield } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { getPlayerStats, getTeams } from "../lib/api";
import { toast } from "sonner";

export default function PlayerStats() {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("goals");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [playersRes, teamsRes] = await Promise.all([
        getPlayerStats(),
        getTeams(),
      ]);
      setPlayers(playersRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      toast.error("Failed to fetch player stats");
    } finally {
      setLoading(false);
    }
  };

  const getTeamLogo = (teamId) => {
    const team = teams.find((t) => t.id === teamId);
    return team?.logo;
  };

  const sortedByGoals = [...players]
    .filter((p) => p.goals > 0)
    .sort((a, b) => b.goals - a.goals);

  const sortedByAssists = [...players]
    .filter((p) => p.assists > 0)
    .sort((a, b) => b.assists - a.assists);

  const sortedByCards = [...players]
    .filter((p) => p.yellow_cards > 0 || p.red_cards > 0)
    .sort((a, b) => b.yellow_cards + b.red_cards * 2 - (a.yellow_cards + a.red_cards * 2));

  const sortedByContributions = [...players]
    .filter((p) => p.goals > 0 || p.assists > 0)
    .sort((a, b) => b.goals + b.assists - (a.goals + a.assists));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const StatRow = ({ player, index, statValue, statLabel }) => (
    <div
      className={`flex items-center gap-4 p-4 rounded-xl bg-card border border-border card-hover animate-fade-in opacity-0`}
      style={{ animationDelay: `${index * 30}ms` }}
      data-testid={`stat-row-${player.player_id}`}
    >
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
          index === 0
            ? "bg-amber-500/20 text-amber-500"
            : index === 1
            ? "bg-slate-400/20 text-slate-400"
            : index === 2
            ? "bg-amber-700/20 text-amber-700"
            : "bg-secondary text-muted-foreground"
        }`}
      >
        {index + 1}
      </div>
      {getTeamLogo(player.team_id) ? (
        <img
          src={getTeamLogo(player.team_id)}
          alt=""
          className="w-10 h-10 rounded-lg object-cover"
        />
      ) : (
        <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
          <Shield className="w-5 h-5 text-muted-foreground" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{player.player_name}</span>
          <span className="text-xs bg-secondary px-2 py-0.5 rounded text-muted-foreground">
            #{player.number}
          </span>
        </div>
        <p className="text-sm text-muted-foreground truncate">
          {player.team_name} • {player.position}
        </p>
      </div>
      <div className="text-right">
        <div className="font-heading text-3xl font-black text-primary">
          {statValue}
        </div>
        <div className="text-xs text-muted-foreground uppercase">{statLabel}</div>
      </div>
    </div>
  );

  return (
    <div data-testid="player-stats-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-extrabold uppercase tracking-tight">
          Player Statistics
        </h1>
        <p className="text-muted-foreground mt-2">
          Top performers across all categories
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-card border border-border grid grid-cols-4 w-full max-w-xl">
          <TabsTrigger value="goals" data-testid="tab-goals">
            <Target className="w-4 h-4 mr-2 hidden sm:block" />
            Goals
          </TabsTrigger>
          <TabsTrigger value="assists" data-testid="tab-assists">
            <UserPlus className="w-4 h-4 mr-2 hidden sm:block" />
            Assists
          </TabsTrigger>
          <TabsTrigger value="cards" data-testid="tab-cards">
            <AlertTriangle className="w-4 h-4 mr-2 hidden sm:block" />
            Cards
          </TabsTrigger>
          <TabsTrigger value="overall" data-testid="tab-overall">
            <User className="w-4 h-4 mr-2 hidden sm:block" />
            Overall
          </TabsTrigger>
        </TabsList>

        {/* Goals */}
        <TabsContent value="goals" className="mt-6">
          {sortedByGoals.length > 0 ? (
            <div className="space-y-3">
              {sortedByGoals.slice(0, 15).map((player, index) => (
                <StatRow
                  key={player.player_id}
                  player={player}
                  index={index}
                  statValue={player.goals}
                  statLabel="Goals"
                />
              ))}
            </div>
          ) : (
            <EmptyState icon={Target} message="No goals scored yet" />
          )}
        </TabsContent>

        {/* Assists */}
        <TabsContent value="assists" className="mt-6">
          {sortedByAssists.length > 0 ? (
            <div className="space-y-3">
              {sortedByAssists.slice(0, 15).map((player, index) => (
                <StatRow
                  key={player.player_id}
                  player={player}
                  index={index}
                  statValue={player.assists}
                  statLabel="Assists"
                />
              ))}
            </div>
          ) : (
            <EmptyState icon={UserPlus} message="No assists recorded yet" />
          )}
        </TabsContent>

        {/* Cards */}
        <TabsContent value="cards" className="mt-6">
          {sortedByCards.length > 0 ? (
            <div className="space-y-3">
              {sortedByCards.slice(0, 15).map((player, index) => (
                <div
                  key={player.player_id}
                  className={`flex items-center gap-4 p-4 rounded-xl bg-card border border-border card-hover animate-fade-in opacity-0`}
                  style={{ animationDelay: `${index * 30}ms` }}
                  data-testid={`card-row-${player.player_id}`}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center font-bold bg-secondary text-muted-foreground">
                    {index + 1}
                  </div>
                  {getTeamLogo(player.team_id) ? (
                    <img
                      src={getTeamLogo(player.team_id)}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">
                        {player.player_name}
                      </span>
                      <span className="text-xs bg-secondary px-2 py-0.5 rounded text-muted-foreground">
                        #{player.number}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {player.team_name} • {player.position}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {player.yellow_cards > 0 && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-6 bg-amber-500 rounded-sm" />
                        <span className="font-bold">{player.yellow_cards}</span>
                      </div>
                    )}
                    {player.red_cards > 0 && (
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-6 bg-red-500 rounded-sm" />
                        <span className="font-bold">{player.red_cards}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={AlertTriangle} message="No cards issued yet" />
          )}
        </TabsContent>

        {/* Overall */}
        <TabsContent value="overall" className="mt-6">
          {sortedByContributions.length > 0 ? (
            <div className="space-y-3">
              {sortedByContributions.slice(0, 15).map((player, index) => (
                <div
                  key={player.player_id}
                  className={`flex items-center gap-4 p-4 rounded-xl bg-card border border-border card-hover animate-fade-in opacity-0`}
                  style={{ animationDelay: `${index * 30}ms` }}
                  data-testid={`overall-row-${player.player_id}`}
                >
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                      index === 0
                        ? "bg-amber-500/20 text-amber-500"
                        : index === 1
                        ? "bg-slate-400/20 text-slate-400"
                        : index === 2
                        ? "bg-amber-700/20 text-amber-700"
                        : "bg-secondary text-muted-foreground"
                    }`}
                  >
                    {index + 1}
                  </div>
                  {getTeamLogo(player.team_id) ? (
                    <img
                      src={getTeamLogo(player.team_id)}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">
                        {player.player_name}
                      </span>
                      <span className="text-xs bg-secondary px-2 py-0.5 rounded text-muted-foreground">
                        #{player.number}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">
                      {player.team_name} • {player.position}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="font-heading text-xl font-black text-primary">
                        {player.goals}
                      </div>
                      <div className="text-xs text-muted-foreground">Goals</div>
                    </div>
                    <div className="text-center">
                      <div className="font-heading text-xl font-black text-accent">
                        {player.assists}
                      </div>
                      <div className="text-xs text-muted-foreground">Assists</div>
                    </div>
                    <div className="text-center">
                      <div className="font-heading text-xl font-black">
                        {player.goals + player.assists}
                      </div>
                      <div className="text-xs text-muted-foreground">Total</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={User} message="No contributions recorded yet" />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const EmptyState = ({ icon: Icon, message }) => (
  <div className="bg-card border border-border rounded-xl p-12 text-center">
    <Icon className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
    <h3 className="font-heading text-xl font-bold uppercase mb-2">
      No Data Yet
    </h3>
    <p className="text-muted-foreground">{message}</p>
  </div>
);
